import type { StateCreator } from 'zustand';
import { loadStored, saveStored, PERSIST_KEYS } from '../hooks/usePersist';
import { getMasterMixes } from '../services/mockData';
import { MUSHROOM_SPECIES } from '../services/thresholds';
import type { GrowBag, MasterMix, Order, Task, LossRecord, PhotoLog, SpawnLot, MushroomSpecies, FlushHarvest, SterilizationLog, CleaningLog } from '../types';
import { recordOperationalEvent } from '../services/operationalData';

export interface GrowSlice {
  growBags: GrowBag[];
  masterMixes: MasterMix[];
  orders: Order[];
  tasks: Task[];
  lossRecords: LossRecord[];
  photoLogs: PhotoLog[];
  spawnLots: SpawnLot[];
  trashedBags: (GrowBag & { deletedAt: string })[];
  customSpecies: MushroomSpecies[];
  sterilizationLogs: SterilizationLog[];
  cleaningLogs: CleaningLog[];

  setGrowBags: (v: GrowBag[] | ((p: GrowBag[]) => GrowBag[])) => void;
  setMasterMixes: (v: MasterMix[] | ((p: MasterMix[]) => MasterMix[])) => void;
  setOrders: (v: Order[] | ((p: Order[]) => Order[])) => void;
  setTasks: (v: Task[] | ((p: Task[]) => Task[])) => void;
  setLossRecords: (v: LossRecord[] | ((p: LossRecord[]) => LossRecord[])) => void;
  setPhotoLogs: (v: PhotoLog[] | ((p: PhotoLog[]) => PhotoLog[])) => void;
  setSpawnLots: (v: SpawnLot[] | ((p: SpawnLot[]) => SpawnLot[])) => void;
  setTrashedBags: (v: ((p: (GrowBag & { deletedAt: string })[]) => (GrowBag & { deletedAt: string })[]) | (GrowBag & { deletedAt: string })[]) => void;
  setCustomSpecies: (v: MushroomSpecies[] | ((p: MushroomSpecies[]) => MushroomSpecies[])) => void;
  setSterilizationLogs: (v: SterilizationLog[] | ((p: SterilizationLog[]) => SterilizationLog[])) => void;
  setCleaningLogs: (v: CleaningLog[] | ((p: CleaningLog[]) => CleaningLog[])) => void;

  generateBagName: (speciesId: string) => string;
  addGrowBag: (bag: Omit<GrowBag, 'id' | 'name'> & { name?: string }) => void;
  addGrowBags: (bagsData: Array<Omit<GrowBag, 'id' | 'name'>>) => void;
  updateGrowBag: (id: string, updates: Partial<GrowBag>) => void;
  deleteGrowBag: (id: string) => void;
  deleteGrowBags: (ids: string[]) => void;
  restoreFromTrash: (bagId: string) => void;
  emptyTrash: () => void;
  permanentlyDeleteBag: (bagId: string) => void;
  addFlushHarvest: (bagId: string, flush: FlushHarvest) => void;
  updateFlushHarvest: (bagId: string, flushNumber: number, updates: Partial<FlushHarvest>) => void;
  addFullBags: (bags: GrowBag[]) => void;
  placeBag: (bagId: string, shelf: string) => void;
  removeBag: (bagId: string, reason: 'harvest' | 'retire' | 'loss') => void;

  addMasterMix: (mix: Omit<MasterMix, 'id'>) => void;
  updateMasterMix: (id: string, updates: Partial<MasterMix>) => void;
  addOrder: (order: Omit<Order, 'id'>) => void;
  updateOrder: (id: string, updates: Partial<Order>) => void;
  addTask: (task: Omit<Task, 'id'>) => void;
  toggleTask: (id: string) => void;
  deleteTask: (id: string) => void;
  addLossRecord: (loss: Omit<LossRecord, 'id'>) => void;
  addPhotoLog: (photo: Omit<PhotoLog, 'id'>) => void;
  addSpawnLot: (lot: Omit<SpawnLot, 'id'>) => void;
  updateSpawnLot: (id: string, updates: Partial<SpawnLot>) => void;
  addCustomSpecies: (sp: MushroomSpecies) => void;
  addSterilizationLog: (log: Omit<SterilizationLog, 'id' | 'createdAt'>) => SterilizationLog;
  updateSterilizationLog: (id: string, updates: Partial<SterilizationLog>) => void;
  deleteSterilizationLog: (id: string) => void;
  linkBagsToSterilization: (sterilizationId: string, bagIds: string[]) => void;
  addCleaningLog: (log: Omit<CleaningLog, 'id' | 'createdAt'>) => void;
  updateCleaningLog: (id: string, updates: Partial<CleaningLog>) => void;
  resetToDemoBags: () => void;
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function makeDemoBags(): GrowBag[] {
  return [
    {
      id: 'demo-1', name: 'İS-001', speciesId: 'istiridye', inoculationDate: daysAgo(49),
      fruitingStartDate: daysAgo(28), status: 'fruiting', position: 'A1', shelf: 'top',
      notes: '', flushCount: 1, totalYieldGrams: 320,
      flushHarvests: [{ flushNumber: 1, harvestDate: daysAgo(15), yieldGrams: 320 }],
    },
    {
      id: 'demo-2', name: 'İS-002', speciesId: 'istiridye', inoculationDate: daysAgo(46),
      fruitingStartDate: daysAgo(25), status: 'fruiting', position: 'A2', shelf: 'top',
      notes: '', flushCount: 0, totalYieldGrams: 0, flushHarvests: [],
    },
    {
      id: 'demo-3', name: 'SH-001', speciesId: 'shiitake', inoculationDate: daysAgo(54),
      fruitingStartDate: daysAgo(21), status: 'fruiting', position: 'B1', shelf: 'middle',
      notes: 'İyi misel büyümesi', flushCount: 2, totalYieldGrams: 510,
      flushHarvests: [
        { flushNumber: 1, harvestDate: daysAgo(13), yieldGrams: 290 },
        { flushNumber: 2, harvestDate: daysAgo(5), yieldGrams: 220 },
      ],
    },
    {
      id: 'demo-4', name: 'SH-002', speciesId: 'shiitake', inoculationDate: daysAgo(42),
      fruitingStartDate: daysAgo(13), status: 'fruiting', position: 'B2', shelf: 'middle',
      notes: '', flushCount: 0, totalYieldGrams: 0, flushHarvests: [],
    },
    {
      id: 'demo-5', name: 'KA-001', speciesId: 'karides', inoculationDate: daysAgo(36),
      status: 'incubation', position: 'C1', shelf: 'bottom',
      notes: 'Yeni parti', flushCount: 0, totalYieldGrams: 0, flushHarvests: [],
    },
    {
      id: 'demo-6', name: 'KA-002', speciesId: 'karides', inoculationDate: daysAgo(32),
      status: 'incubation', position: 'C2', shelf: 'bottom',
      notes: '', flushCount: 0, totalYieldGrams: 0, flushHarvests: [],
    },
    {
      id: 'demo-7', name: 'İS-003', speciesId: 'istiridye', inoculationDate: daysAgo(72),
      fruitingStartDate: daysAgo(54), harvestDate: daysAgo(18), status: 'harvested',
      position: 'A3', shelf: 'top', notes: 'Döngü tamamlandı', flushCount: 3, totalYieldGrams: 870,
      flushHarvests: [
        { flushNumber: 1, harvestDate: daysAgo(42), yieldGrams: 340 },
        { flushNumber: 2, harvestDate: daysAgo(31), yieldGrams: 300 },
        { flushNumber: 3, harvestDate: daysAgo(18), yieldGrams: 230 },
      ],
    },
  ];
}

export const createGrowSlice: StateCreator<GrowSlice, [], [], GrowSlice> = (set, get) => ({
  growBags: loadStored(PERSIST_KEYS.GROW_BAGS, makeDemoBags()),
  masterMixes: loadStored(PERSIST_KEYS.MASTER_MIXES, getMasterMixes()),
  orders: loadStored(PERSIST_KEYS.ORDERS, []),
  tasks: loadStored(PERSIST_KEYS.TASKS, []),
  lossRecords: loadStored(PERSIST_KEYS.LOSS_RECORDS, []),
  photoLogs: loadStored(PERSIST_KEYS.PHOTO_LOGS, []),
  spawnLots: loadStored(PERSIST_KEYS.SPAWN_LOTS, []),
  trashedBags: (() => {
    const TRASH_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 gün
    const all = loadStored<(GrowBag & { deletedAt: string })[]>(PERSIST_KEYS.TRASHED_BAGS, []);
    return all.filter(b => Date.now() - new Date(b.deletedAt).getTime() < TRASH_TTL_MS);
  })(),
  customSpecies: loadStored(PERSIST_KEYS.CUSTOM_SPECIES, []),
  sterilizationLogs: loadStored<SterilizationLog[]>('sterilization_logs', []),
  cleaningLogs: loadStored<CleaningLog[]>('cleaning_logs', []),

  setSterilizationLogs: (v) => set(state => {
    const next = typeof v === 'function' ? v(state.sterilizationLogs) : v;
    saveStored('sterilization_logs', next);
    return { sterilizationLogs: next };
  }),
  setCleaningLogs: (v) => set(state => {
    const next = typeof v === 'function' ? v(state.cleaningLogs) : v;
    saveStored('cleaning_logs', next);
    return { cleaningLogs: next };
  }),

  addSterilizationLog: (log) => {
    const newLog: SterilizationLog = {
      ...log,
      id: `steril-${crypto.randomUUID()}`,
      createdAt: new Date().toISOString(),
    };
    get().setSterilizationLogs(prev => [newLog, ...prev]);
    return newLog;
  },
  updateSterilizationLog: (id, updates) => get().setSterilizationLogs(prev =>
    prev.map(l => l.id === id ? { ...l, ...updates } : l)
  ),
  deleteSterilizationLog: (id) => get().setSterilizationLogs(prev =>
    prev.filter(l => l.id !== id)
  ),
  linkBagsToSterilization: (sterilizationId, bagIds) => get().setSterilizationLogs(prev =>
    prev.map(l => l.id === sterilizationId
      ? { ...l, linkedBagIds: [...new Set([...l.linkedBagIds, ...bagIds])] }
      : l
    )
  ),

  addCleaningLog: (log) => {
    const newLog: CleaningLog = {
      ...log,
      id: `clean-${crypto.randomUUID()}`,
      createdAt: new Date().toISOString(),
    };
    get().setCleaningLogs(prev => [newLog, ...prev]);
  },
  updateCleaningLog: (id, updates) => get().setCleaningLogs(prev =>
    prev.map(l => l.id === id ? { ...l, ...updates } : l)
  ),

  setGrowBags: (v) => set(state => {
    const next = typeof v === 'function' ? v(state.growBags) : v;
    saveStored(PERSIST_KEYS.GROW_BAGS, next);
    return { growBags: next };
  }),
  setMasterMixes: (v) => set(state => {
    const next = typeof v === 'function' ? v(state.masterMixes) : v;
    saveStored(PERSIST_KEYS.MASTER_MIXES, next);
    return { masterMixes: next };
  }),
  setOrders: (v) => set(state => {
    const next = typeof v === 'function' ? v(state.orders) : v;
    saveStored(PERSIST_KEYS.ORDERS, next);
    return { orders: next };
  }),
  setTasks: (v) => set(state => {
    const next = typeof v === 'function' ? v(state.tasks) : v;
    saveStored(PERSIST_KEYS.TASKS, next);
    return { tasks: next };
  }),
  setLossRecords: (v) => set(state => {
    const next = typeof v === 'function' ? v(state.lossRecords) : v;
    saveStored(PERSIST_KEYS.LOSS_RECORDS, next);
    return { lossRecords: next };
  }),
  setPhotoLogs: (v) => set(state => {
    const next = typeof v === 'function' ? v(state.photoLogs) : v;
    saveStored(PERSIST_KEYS.PHOTO_LOGS, next);
    return { photoLogs: next };
  }),
  setSpawnLots: (v) => set(state => {
    const next = typeof v === 'function' ? v(state.spawnLots) : v;
    saveStored(PERSIST_KEYS.SPAWN_LOTS, next);
    return { spawnLots: next };
  }),
  setTrashedBags: (v) => set(state => {
    const next = typeof v === 'function' ? v(state.trashedBags) : v;
    saveStored(PERSIST_KEYS.TRASHED_BAGS, next);
    return { trashedBags: next };
  }),
  setCustomSpecies: (v) => set(state => {
    const next = typeof v === 'function' ? v(state.customSpecies) : v;
    saveStored(PERSIST_KEYS.CUSTOM_SPECIES, next);
    return { customSpecies: next };
  }),

  generateBagName: (speciesId: string) => {
    const { growBags } = get();
    const species = MUSHROOM_SPECIES.find(s => s.id === speciesId);
    const prefix = species?.prefix || 'XX';
    const existingCount = growBags.filter(b => b.speciesId === speciesId).length;
    const num = (existingCount + 1).toString().padStart(3, '0');
    return `${prefix}-${num}`;
  },
  
  addGrowBag: (bag) => get().setGrowBags(prev => {
    const species = MUSHROOM_SPECIES.find(s => s.id === bag.speciesId);
    const prefix = species?.prefix || 'XX';
    const existingCount = prev.filter(b => b.speciesId === bag.speciesId).length;
    const name = bag.name || `${prefix}-${(existingCount + 1).toString().padStart(3, '0')}`;
    return [...prev, { ...bag, id: `bag-${crypto.randomUUID()}`, name, flushHarvests: bag.flushHarvests || [], totalYieldGrams: bag.totalYieldGrams || 0 }];
  }),

  addGrowBags: (bagsData) => get().setGrowBags(prev => {
    const next = [...prev];
    bagsData.forEach((bagData) => {
      const species = MUSHROOM_SPECIES.find(s => s.id === bagData.speciesId);
      const prefix = species?.prefix || 'XX';
      const existingCount = next.filter(b => b.speciesId === bagData.speciesId).length;
      const name = `${prefix}-${(existingCount + 1).toString().padStart(3, '0')}`;
      next.push({ ...bagData, id: `bag-${crypto.randomUUID()}`, name, flushHarvests: bagData.flushHarvests || [], totalYieldGrams: bagData.totalYieldGrams || 0 });
    });
    return next;
  }),

  updateGrowBag: (id, updates) => get().setGrowBags(prev => prev.map(b => b.id === id ? { ...b, ...updates } : b)),
  
  deleteGrowBag: (id) => {
    const deletedAt = new Date().toISOString();
    const { growBags, setTrashedBags, setGrowBags } = get();
    const bag = growBags.find(b => b.id === id);
    if (bag) {
      setTrashedBags(prevTrash => (
        prevTrash.some(item => item.id === bag.id)
          ? prevTrash
          : [...prevTrash, { ...bag, deletedAt }]
      ));
    }
    setGrowBags(prev => prev.filter(b => b.id !== id));
  },

  deleteGrowBags: (ids) => {
    const deletedAt = new Date().toISOString();
    const { growBags, setTrashedBags, setGrowBags } = get();
    const toTrash = growBags.filter(b => ids.includes(b.id));
    setTrashedBags(prevTrash => {
      const existing = new Set(prevTrash.map(b => b.id));
      const newItems = toTrash.filter(b => !existing.has(b.id)).map(b => ({ ...b, deletedAt }));
      return [...prevTrash, ...newItems];
    });
    setGrowBags(prev => prev.filter(b => !ids.includes(b.id)));
  },

  restoreFromTrash: (bagId: string) => {
    const { trashedBags, setTrashedBags, setGrowBags } = get();
    const bag = trashedBags.find(b => b.id === bagId);
    if (bag) {
      const { deletedAt: _dt, ...rest } = bag;
      setGrowBags(g => (g.some(item => item.id === rest.id) ? g : [...g, rest]));
    }
    setTrashedBags(prev => prev.filter(b => b.id !== bagId));
  },

  emptyTrash: () => get().setTrashedBags([]),
  permanentlyDeleteBag: (bagId: string) => get().setTrashedBags(prev => prev.filter(b => b.id !== bagId)),

  addFlushHarvest: (bagId, flush) => get().setGrowBags(prev => prev.map(b => {
    if (b.id !== bagId) return b;
    const flushHarvests = [...b.flushHarvests, flush];
    const totalYieldGrams = flushHarvests.reduce((sum, f) => sum + f.yieldGrams, 0);
    return { ...b, flushHarvests, totalYieldGrams, flushCount: flushHarvests.length, status: 'harvested', harvestDate: flush.harvestDate };
  })),

  updateFlushHarvest: (bagId, flushNumber, updates) => get().setGrowBags(prev => prev.map(b => {
    if (b.id !== bagId) return b;
    const flushHarvests = b.flushHarvests.map(fh =>
      fh.flushNumber === flushNumber ? { ...fh, ...updates } : fh
    );
    const totalYieldGrams = flushHarvests.reduce((sum, f) => sum + f.yieldGrams, 0);
    return { ...b, flushHarvests, totalYieldGrams };
  })),

  addFullBags: (bags) => get().setGrowBags(prev => [...prev, ...bags]),

  placeBag: (bagId, shelf) => {
    const bag = get().growBags.find(b => b.id === bagId);
    const placedAt = new Date().toISOString();
    get().setGrowBags(prev => prev.map(b => b.id === bagId
      ? { ...b, shelf, placedBy: 'qr_scan', placedAt, lastSeenAt: placedAt, status: b.status === 'incubation' ? 'fruiting' as const : b.status }
      : b
    ));
    if (bag) {
      recordOperationalEvent({
        eventType: 'qr_place',
        bagId,
        bagName: bag.name,
        summary: `${bag.name} ${shelf} rafına yerleştirildi`,
        payload: { shelf, previousStatus: bag.status },
      });
    }
  },

  removeBag: (bagId, reason) => {
    const bag = get().growBags.find(b => b.id === bagId);
    const seenAt = new Date().toISOString();
    get().setGrowBags(prev => prev.map(b => b.id === bagId
      ? { ...b, shelf: undefined, lastSeenAt: seenAt, status: reason === 'harvest' ? 'harvested' as const : 'retired' as const }
      : b
    ));
    if (bag) {
      recordOperationalEvent({
        eventType: 'qr_remove',
        bagId,
        bagName: bag.name,
        summary: `${bag.name} kabinden çıkarıldı`,
        payload: { reason, previousShelf: bag.shelf, previousStatus: bag.status },
      });
    }
  },

  addMasterMix: (mix) => get().setMasterMixes(prev => [...prev, { ...mix, id: `mix-${crypto.randomUUID()}` }]),
  updateMasterMix: (id, updates) => get().setMasterMixes(prev => prev.map(m => m.id === id ? { ...m, ...updates } : m)),
  addOrder: (order) => get().setOrders(prev => [...prev, { ...order, id: `ord-${crypto.randomUUID()}` }]),
  updateOrder: (id, updates) => get().setOrders(prev => prev.map(o => o.id === id ? { ...o, ...updates } : o)),
  addTask: (task) => get().setTasks(prev => [...prev, { ...task, id: `task-${crypto.randomUUID()}` }]),
  toggleTask: (id) => get().setTasks(prev => prev.map(t => t.id === id ? { ...t, completed: !t.completed } : t)),
  deleteTask: (id) => get().setTasks(prev => prev.filter(t => t.id !== id)),
  addLossRecord: (loss) => get().setLossRecords(prev => [...prev, { ...loss, id: `loss-${crypto.randomUUID()}` }]),
  addPhotoLog: (photo) => {
    const entry = { ...photo, id: `photo-${crypto.randomUUID()}` };
    get().setPhotoLogs(prev => [...prev, entry]);
    const bag = get().growBags.find(b => b.id === photo.bagId);
    recordOperationalEvent({
      eventType: photo.captureSource ?? 'photo_log',
      bagId: photo.bagId,
      bagName: bag?.name,
      summary: `${bag?.name ?? 'Torba'} için görsel kayıt eklendi`,
      payload: { description: photo.description, stage: photo.stage },
    });
  },
  addSpawnLot: (lot) => get().setSpawnLots(prev => [...prev, { ...lot, id: `spawn-${crypto.randomUUID()}` }]),
  updateSpawnLot: (id, updates) => get().setSpawnLots(prev => prev.map(l => l.id === id ? { ...l, ...updates } : l)),
  addCustomSpecies: (sp) => get().setCustomSpecies(prev => {
    if (prev.find(s => s.id === sp.id)) return prev;
    return [...prev, sp];
  }),
  resetToDemoBags: () => get().setGrowBags(makeDemoBags()),
});
